import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { CopyField } from "@aurumiq/ui/composite/copy-button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Field, TextField } from "@/components/field";
import type { ApiKeyCreated } from "@/lib/types";
import { useCreateApiKey } from "./queries";

export const API_KEY_SCOPES = ["releases:write", "cms:write", "telemetry:read"] as const;

const schema = z.object({
  name: z.string().trim().min(2, "Name is required"),
  scopes: z.array(z.enum(API_KEY_SCOPES)).min(1, "Pick at least one scope"),
});
type FormValues = z.infer<typeof schema>;

export function ApiKeyDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const create = useCreateApiKey();
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", scopes: [] },
  });
  const err = (k: keyof FormValues) => form.formState.errors[k]?.message;
  const close = (o: boolean) => {
    if (!o) {
      form.reset();
      setCreated(null);
    }
    onOpenChange(o);
  };
  const onSubmit = form.handleSubmit(async (v) => setCreated(await create.mutateAsync(v)));
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        {created ? (
          <div className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Key created</DialogTitle>
              <DialogDescription>
                Copy it now. <b>It will not be shown again.</b>
              </DialogDescription>
            </DialogHeader>
            <CopyField value={created.key} label="Copy key" />
            <p className="text-muted-foreground text-xs">
              {created.name} · {created.scopes.join(", ")}
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Create API key</DialogTitle>
              <DialogDescription>
                For CI and integrations. Scopes limit what the key can do.
              </DialogDescription>
            </DialogHeader>
            <TextField
              label="Name"
              placeholder="e.g. GitHub releases"
              error={err("name")}
              {...form.register("name")}
            />
            <Field label="Scopes" error={err("scopes")}>
              <div className="grid gap-1.5">
                {API_KEY_SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      value={s}
                      className="size-4 accent-primary"
                      {...form.register("scopes")}
                    />
                    <span className="font-mono text-xs">{s}</span>
                  </label>
                ))}
              </div>
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create key"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
